import { FilterMatchType } from "../../../enums/FilterMatchType";
import { RelatedFilterValidation } from "../../../enums/RelatedFilterValidation";
import { AccountSchema } from "./Schemas/AccountSchema";
import { EstablishmentsSchema } from "./Schemas/EstablishmentsSchema";
import { AuditSchema } from "./Schemas/AuditSchema";
import { CaseMaterialsSchema } from "./Schemas/CaseMaterialsSchema";
import { CasesSchema } from "./Schemas/CasesSchema";
import { ContainersSchema } from "./Schemas/ContainersSchema";
import { CustomerSchema } from "./Schemas/CustomerSchema";
import { DeliveryMethodSchema } from "./Schemas/DeliveryMethodSchema";
import { OperatorSettingsSchema } from "./Schemas/OperatorSettingsSchema";
import { OperatorsSchema } from "./Schemas/OperatorsSchema";
import { OuterCubeSchema } from "./Schemas/OuterCubeSchema";
import { PackingBoxSchema } from "./Schemas/PackingBoxSchema";
import { PrefixSchema } from "./Schemas/PrefixSchema";
import { RangesAndCasesSchema } from "./Schemas/RangesAndCasesSchema";
import { RangesSchema } from "./Schemas/RangesSchema";
import { SpecimenSchema } from "./Schemas/SpecimenSchema";
import { UserSchema } from "./Schemas/UserSchema";
import { VolumeSchema } from "./Schemas/VolumeSchema";
import { AddressSchema } from "./Schemas/AddressSchema";
import { FileUploadSchema } from "./Schemas/FileUploadSchema";
import { IForm } from "./Form/Form";
import { AccountAddressSchema } from "./Schemas/AccountAddressSchema";
import { ReportSchema } from "./Schemas/ReportsSchema";
import { CustomerRecallsSchema } from "./Schemas/CustomerRecallsSchema";
import { OperatorRecallsSchema } from "./Schemas/OperatorRecallsSchema";
import { RecallsSchema } from "./Schemas/RecallsSchema";
import { RecallLineItemsSchema } from "./Schemas/RecallsLineItemsSchema";
import { ReactElement } from "react";
import { OperatorUpliftSchema } from "./Schemas/OperatorUpliftSchema";
import { CustomerReturnsSchema } from "./Schemas/CustomerReturnsSchema";
import { ReturnsSchema } from "./Schemas/ReturnsSchema";
import { ReturnLineItemsSchema } from "./Schemas/ReturnLineItemsSchema";
import { WorkstationSchema } from "./Schemas/WorkstationSchema";
import { WorkstationOuterCubesSchema } from "./Schemas/WorkstationOuterCubesSchema";
import { PartFullOuterCubesSchema } from "./Schemas/PartFullOuterCubesSchema";
import { GlobalInvoicesSchema } from "./Schemas/GlobalInvoicesSchema";
import { GlobalUsersSchema } from "./Schemas/GlobalUsersSchema";
import { AccountSpecifcInvoicesSchema } from "./Schemas/AccountSpecifcInvoicesSchema";
import { CostMultipliersSchema } from "./Schemas/CostMultipliersSchema";
import { DisposalsSchema } from "./Schemas/DisposalsSchema";
import { DisposalsLineItemsSchema } from "./Schemas/DisposalsLineItemsSchema";
import { DisposalChecksSchema } from "./Schemas/DisposalChecksSchema";
import { AccountOrderNumbersSchema } from "./Schemas/AccountOrderNumbersSchema";
import { CaseMaterialTypesSchema } from "./Schemas/CaseMaterialTypesSchema";
import { LocationsSchema } from "./Schemas/LocationsSchema";
import { ISelected } from "../List/List";
import { PermissionContextType } from "../../../modules/auth/Permissions/PermissionContext";

export interface ISchemaProperty {
    name: string,
    displayName: string,
    descriptor?: boolean,
    group?: string | number,
    type: string,
    key: boolean,
    showInList: boolean,
    showInForm: boolean,
    showInView: boolean,
    requiredInForm: boolean,
    children: ISchemaProperty[],
    entityName?: string,
    validateRule?: Function,
    validationError?: string,
    computeFunction?: Function
    computeProperties?: string[],
    className?: string,
    defaultDisplayValue?: string,
    formValueResolver?: (form: IForm) => string,
    rememberLastFormValue?: boolean,
    valueFormatter?: (val: any) => string,
    renderBasedOnParentData?: (parentValues: any, isCreate: boolean) => boolean,
    requiredInFormIf?: (parentValues: any) => boolean,
    addOnly?: boolean,
    editOnly?: boolean,
    autocomplete?: string
    inputType?: string
}

export interface ISchemaFilter {
    PropertyName: string,
    displayName: string,
    displayNameFactory?: ((config: MainConfig) => string) | undefined,
    placeHolder?: string | undefined,
    required: boolean,
    type: string,
    propertyType: string,
    className: string
    matchType: FilterMatchType,
    relatedFilter?: string,
    relatedFilterValidation?: RelatedFilterValidation,
    entityName?: string,
    onValue?: string | number | boolean | undefined,
    offValue?: string | number | boolean | undefined,
    enum?: any,
    showInTab: boolean,
    permissionToView?: ((Permissions: PermissionContextType) => boolean),
    dependsOn: string | null
}

export interface ISchemaChild {
    entityName: string,
    displayName: string,
    viewType: string,
    cantView?: boolean,
    permissionToView?: ((Permissions: PermissionContextType, accountId?: string | undefined) => boolean),
    enableFilters: boolean,
    navPropertyName?: string,
    canAdd: boolean | ((Permissions: PermissionContextType, accountId?: string | undefined) => boolean),
    dataType?: string | null | undefined,
    alwaysShowheader?: boolean | undefined
}

export interface ISchemaAction {
    text: string,
    action: string
    style: string,
    icon: any,
    show?: ((data: any) => boolean),
    isEnabled?: ((selected: ISelected[]) => boolean),
    permissionToView?: ((Permissions: PermissionContextType) => boolean)
};

export interface MainConfig {
    isNewTimePeriodMinutes: number;
    itemCountExplanation: string;
    digitalRecallsEnabled: boolean;
    /** BCP 47; optional until API provides it — DateHelper falls back to en-GB. */
    culture?: string;
    /** IANA TZDB id; optional until API provides it — DateHelper falls back to Europe/London. */
    timeZoneId?: string;
}

export interface ISchema {
    displayName: string,
    resultsKey?: string
    httpContentType?: string | undefined | null,
    props: ISchemaProperty[],
    primaryFilter?: ISchemaFilter,
    filters?: ISchemaFilter[],
    children?: ISchemaChild[]
    sidePanelActions?: ISchemaAction[]
    listActions?: ISchemaAction[]
    GetSidePanelComponent?: ((data: any, onViewPage: boolean, entityName?: string) => ReactElement),
    onAdd?: Function | undefined,
    editWarning?: string,
    editButtonText?: string
}

interface ISchemas {
    cases: ISchema
    rangesAndCases: ISchema
    containers: ISchema
    audits: ISchema
    outerCubes: ISchema
    ranges: ISchema
    packingBoxes: ISchema
    specimens: ISchema
    caseMaterials: ISchema
    customers: ISchema
    accounts: ISchema
    users: ISchema
    operatorSettings: ISchema
    operators: ISchema
    establishments: ISchema
    volumes: ISchema,
    deliveryMethods: ISchema,
    prefixes: ISchema,
    addresses: ISchema,
    fileUploads: ISchema,
    accountAddresses: ISchema,
    accountOrderNumbers: ISchema,
    inventoryExports: ISchema,
    recallLineItems: ISchema,
    recalls: ISchema,
    customerRecalls: ISchema,
    operatorRecalls: ISchema,
    operatorUplifts: ISchema,
    customerReturns: ISchema,
    returns: ISchema,
    returnLineItems: ISchema,
    workstations: ISchema,
    workstationOuterCubes: ISchema,
    partFullOuterCubes: ISchema,
    globalInvoices: ISchema,
    accountInvoices: ISchema,
    costMultipliers: ISchema,
    disposals: ISchema,
    disposalLineItems: ISchema,
    disposalChecks: ISchema,
    caseMaterialTypes: ISchema,
    locations: ISchema,
    globalUsers: ISchema
}

let Schemas: ISchemas = {
    cases: CasesSchema,
    rangesAndCases: RangesAndCasesSchema,
    containers: ContainersSchema,
    audits: AuditSchema,
    outerCubes: OuterCubeSchema,
    ranges: RangesSchema,
    packingBoxes: PackingBoxSchema,
    specimens: SpecimenSchema,
    caseMaterials: CaseMaterialsSchema,
    customers: CustomerSchema,
    accounts: AccountSchema,
    users: UserSchema,
    operatorSettings: OperatorSettingsSchema,
    operators: OperatorsSchema,
    establishments: EstablishmentsSchema,
    volumes: VolumeSchema,
    deliveryMethods: DeliveryMethodSchema,
    prefixes: PrefixSchema,
    addresses: AddressSchema,
    fileUploads: FileUploadSchema,
    accountAddresses: AccountAddressSchema,
    accountOrderNumbers: AccountOrderNumbersSchema,
    inventoryExports: ReportSchema,
    recallLineItems: RecallLineItemsSchema,
    recalls: RecallsSchema,
    customerRecalls: CustomerRecallsSchema,
    operatorRecalls: OperatorRecallsSchema,
    operatorUplifts: OperatorUpliftSchema,
    customerReturns: CustomerReturnsSchema,
    returns: ReturnsSchema,
    returnLineItems: ReturnLineItemsSchema,
    workstations: WorkstationSchema,
    workstationOuterCubes: WorkstationOuterCubesSchema,
    partFullOuterCubes: PartFullOuterCubesSchema,
    globalInvoices: GlobalInvoicesSchema,
    accountInvoices: AccountSpecifcInvoicesSchema,
    costMultipliers: CostMultipliersSchema,
    disposals: DisposalsSchema,
    disposalLineItems: DisposalsLineItemsSchema,
    disposalChecks: DisposalChecksSchema,
    caseMaterialTypes: CaseMaterialTypesSchema,
    locations: LocationsSchema,
    globalUsers: GlobalUsersSchema
}

export default function GetSchema(key: string, config?: MainConfig): ISchema {
    const schema = Schemas[key as keyof typeof Schemas];
    
    // Apply displayNameFactory to filters if config is provided
    if (config && schema.filters) {
        schema.filters = schema.filters.map(filter => {
            if (filter.displayNameFactory) {
                return {
                    ...filter,
                    displayName: filter.displayNameFactory(config)
                };
            }
            return filter;
        });
    }
    
    // Apply displayNameFactory to primaryFilter if config is provided
    if (config && schema.primaryFilter?.displayNameFactory) {
        schema.primaryFilter = {
            ...schema.primaryFilter,
            displayName: schema.primaryFilter.displayNameFactory(config)
        };
    }
    
    return schema;
}

/**
 * Checks if an entity has any visible children based on permissions
 * @param entityName The entity name to check
 * @param permissions The user's permissions
 * @returns true if there are visible children, false otherwise
 */
export function hasVisibleChildren(entityName: string, ctx: PermissionContextType): boolean {
    const schema = GetSchema(entityName);
    if (!schema.children || !ctx) {
        // If no children defined, allow view (some entities might not have children)
        return true;
    }
    return schema.children.some(child =>
        child.permissionToView === undefined || child.permissionToView(ctx)
    );
}